/**
 * Lettura delle righe di log dei container.
 *
 * Molti programmi colorano l'output con le sequenze ANSI (`ESC[35m…ESC[39m`):
 * nel browser arrivano come caratteri di controllo e sporcano la riga. Qui le
 * si traduce in segmenti con un colore, e si riconosce il livello della riga
 * (errore, avviso, …) per poterla evidenziare e filtrare.
 */

export type Livello = "errore" | "avviso" | "info" | "debug";

export interface Segmento {
  readonly text: string;
  /** Indice nella tavolozza ANSI a 16 colori, null per il colore predefinito. */
  readonly colore: number | null;
  readonly grassetto: boolean;
  readonly tenue: boolean;
}

// ponytail: solo le sequenze CSI (ESC[…lettera); OSC e simili restano rare nei log
const SEQUENZA = /\x1b\[([0-9;]*)([A-Za-z])/g;

/** Spezza la riga in segmenti colorati; il testo concatenato è privo di sequenze. */
export function segmentaAnsi(riga: string): Segmento[] {
  const segmenti: Segmento[] = [];
  let colore: number | null = null;
  let grassetto = false;
  let tenue = false;
  let ultimo = 0;

  const aggiungi = (text: string) => {
    // Un ESC isolato o una sequenza tronca non devono finire a schermo.
    text = text.replace(/\x1b/g, "");
    if (text) segmenti.push({ text, colore, grassetto, tenue });
  };

  for (const m of riga.matchAll(SEQUENZA)) {
    aggiungi(riga.slice(ultimo, m.index));
    ultimo = m.index + m[0].length;
    if (m[2] !== "m") continue; // movimenti del cursore e simili: si scartano

    const codici = (m[1] || "0").split(";").map(Number);
    for (let i = 0; i < codici.length; i++) {
      const c = codici[i]!;
      if (c === 0) [colore, grassetto, tenue] = [null, false, false];
      else if (c === 1) grassetto = true;
      else if (c === 2) tenue = true;
      else if (c === 22) [grassetto, tenue] = [false, false];
      else if (c >= 30 && c <= 37) colore = c - 30;
      else if (c >= 90 && c <= 97) colore = c - 90 + 8;
      else if (c === 39) colore = null;
      else if (c === 38 || c === 48) {
        // Colori estesi: 256 colori (5;n) o RGB (2;r;g;b). Si tiene solo la
        // parte che cade nella tavolozza base, il resto torna al predefinito.
        const n = codici[i + 2];
        if (c === 38) colore = codici[i + 1] === 5 && n !== undefined && n < 16 ? n : null;
        i += codici[i + 1] === 5 ? 2 : 4;
      }
    }
  }

  aggiungi(riga.slice(ultimo));
  return segmenti;
}

const PAROLE: Record<string, Livello> = {
  error: "errore", err: "errore", fatal: "errore", panic: "errore", critical: "errore", crit: "errore",
  emerg: "errore", alert: "errore", severe: "errore", exception: "errore",
  warn: "avviso", warning: "avviso",
  info: "info", notice: "info",
  debug: "debug", trace: "debug", verbose: "debug",
};

const PAROLA_LIVELLO = new RegExp(`\\b(${Object.keys(PAROLE).join("|")})\\b`, "i");

/**
 * Livello della riga, dalla prima parola di livello che compare.
 *
 * La prima e non una qualunque: "INFO nessun error trovato" è informativa.
 * Copre i formati comuni — `[WARN]`, `level=warn`, `"level":"warn"`, `ERROR:`.
 */
export function livelloDi(testo: string): Livello | null {
  const m = PAROLA_LIVELLO.exec(testo);
  return m ? PAROLE[m[1]!.toLowerCase()]! : null;
}
